/**
 * A porta entre a tela de combustível e a API do módulo 08.
 *
 * É o único arquivo do módulo que conhece `fetch`, caminho de rota e formato de
 * JSON. Quem está acima dele — `consultas.ts` e as telas — fala em domínio.
 *
 * ## O que acontece quando não há sessão
 *
 * A autenticação existe (migração 0009). Um **401** quer dizer que este
 * navegador não tem sessão válida: ninguém entrou, ou o prazo de 12 horas
 * passou com a aba aberta. Isso tem erro com nome próprio (`SemSessao`),
 * separado de `ApiIndisponivel` (backend fora do ar) e de `RecusaDaApi` (o
 * pedido chegou e foi recusado, com motivo).
 *
 * Não há mais queda para massa de demonstração: ela foi apagada nesta rodada,
 * e com ela o risco de a tela mostrar número inventado com cara de número real.
 * `SemSessao` vira "entre outra vez" na tela; `ApiIndisponivel` vira "o sistema
 * não respondeu"; `RecusaDaApi` vira mensagem no formulário, que é onde a
 * pessoa pode fazer algo a respeito.
 *
 * ## De onde vem o endereço da API
 *
 * De `@/lib/api`, nunca daqui. Em desenvolvimento ele é `/api`, que o proxy do
 * Vite reescreve para o Fastify e cujo prefixo ele tira; num container é o
 * endereço completo da API daquele ambiente, assado no build. O caminho de
 * cada rota é o mesmo nos dois lugares, então nenhuma tela muda de ambiente
 * para ambiente.
 */

import { buscar } from '@/lib/licenca-das-rotas';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';

/**
 * A base e as credenciais vem de `@/lib/api`, num lugar so.
 *
 * Vazio quer dizer `/api`, que o proxy do Vite reescreve em
 * desenvolvimento; num container ela e o endereco completo da API, assado
 * no build por `VITE_API_URL`. Nenhum nome de maquina mora neste arquivo.
 */
const BASE = BASE_DA_API;

/** Não há cookie de sessão válido: ninguém entrou, ou o prazo expirou. */
export class SemSessao extends Error {
  constructor() {
    super('não há sessão aberta');
    this.name = 'SemSessao';
  }
}

/** O backend não respondeu. Rede, porta fechada, processo fora do ar. */
export class ApiIndisponivel extends Error {
  constructor() {
    super('o sistema não respondeu');
    this.name = 'ApiIndisponivel';
  }
}

/**
 * O pedido chegou e foi recusado — e a recusa vem com o que fazer em seguida.
 *
 * As duas frases são as que o servidor escreveu. A tela não as reescreve: quem
 * conhece a regra é quem a aplicou, e traduzir de novo aqui produziria duas
 * versões da mesma mensagem, que divergem na primeira correção.
 */
export class RecusaDaApi extends Error {
  readonly comoResolver: string;
  readonly status: number;

  constructor(erro: string, comoResolver: string, status: number) {
    super(erro);
    this.name = 'RecusaDaApi';
    this.comoResolver = comoResolver;
    this.status = status;
  }
}

interface CorpoDeErro {
  readonly erro?: string;
  readonly comoResolver?: string;
}

/**
 * A frase para quando o SERVIDOR NÃO MANDOU UMA.
 *
 * A queda antiga era uma só, e era escrita para um lançamento: *"Confira os
 * campos e tente outra vez"*. Numa lista não há campo nenhum para conferir — e
 * mandar a pessoa conferir campos que a tela não tem é pior do que não dizer
 * nada: ela procura o erro onde ele não está, e conclui que o sistema é
 * confuso quando na verdade a mensagem é que estava errada.
 *
 * Foi visto na tela de cadastros: com o servidor de desenvolvimento rodando um
 * processo ANTERIOR às rotas novas, o `GET` respondia 404, e a tela dizia
 * "Confira os campos". Não havia campo, e nada que a pessoa fizesse ali
 * resolveria — o que faltava era reiniciar o servidor.
 */
function quedaDoErro(status: number): { erro: string; comoResolver: string } {
  if (status === 403) {
    return {
      erro: 'você não tem acesso a isto',
      comoResolver: 'Peça a quem administra o sistema para liberar esta ação para o seu perfil.',
    };
  }
  if (status === 404) {
    return {
      erro: 'o servidor não conhece este endereço',
      comoResolver:
        'Recarregue a página. Se o sistema acabou de ser atualizado, o servidor ainda está com a versão anterior e precisa ser reiniciado.',
    };
  }
  if (status >= 500) {
    return {
      erro: 'o servidor falhou ao responder',
      comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
    };
  }
  return {
    erro: 'o pedido não foi aceito',
    comoResolver: 'Confira o que foi preenchido e tente outra vez; se continuar, avise o suporte com o horário.',
  };
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  let resposta: Response;
  try {
    /* A leitura sem a licenca da rota nem sai: ver `@/lib/licenca-das-rotas`. */
    resposta = await buscar(`${BASE}${caminho}`, caminho, {
      credentials: CREDENCIAIS_DA_API,
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiIndisponivel();
  }

  if (resposta.status === 401) throw new SemSessao();

  if (!resposta.ok) {
    /* Corpo ilegível não pode virar tela em branco: a recusa existe de qualquer
       jeito, e a pessoa precisa saber o que fazer com ela. */
    const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
    throw new RecusaDaApi(
      corpo.erro ?? quedaDoErro(resposta.status).erro,
      corpo.comoResolver ?? quedaDoErro(resposta.status).comoResolver,
      resposta.status,
    );
  }

  return (await resposta.json()) as T;
}

function enviar<T>(caminho: string, corpo: unknown): Promise<T> {
  return pedir<T>(caminho, { method: 'POST', body: JSON.stringify(corpo) });
}

/**
 * `PATCH` manda SÓ o que mudou — e é por isso que ele existe separado do POST.
 *
 * O motor de cadastro do backend grava as colunas que o corpo alimenta, e só
 * elas. Mandar o formulário inteiro num `PATCH` faria toda gravação reescrever
 * todos os campos, inclusive os que ninguém tocou — e a trilha de auditoria
 * registraria uma alteração em cada um deles, todas as vezes. A trilha existe
 * para dizer o que mudou; enchê-la de campos que não mudaram é apagar o sinal
 * com ruído.
 */
function alterar<T>(caminho: string, corpo: unknown): Promise<T> {
  return pedir<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) });
}


/* -------------------------------------------------- os cadastros do módulo */

/** O tanque como o CADASTRO o devolve — não é o mesmo que `TanqueDaApi`. */
export interface TanqueCadastradoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly tipo: string;
  readonly produtoId: string;
  readonly produto: string;
  readonly produtoCategoria: string;
  readonly produtoUnidade: string;
  readonly capacidadeL: number;
  readonly toleranciaL: number;
  readonly toleranciaPct: number;
  readonly obraId: string | null;
  readonly obra: string | null;
  readonly filialId: string | null;
  readonly filial: string | null;
  readonly padrao: boolean;
  readonly ativo: boolean;
}

export interface PostoCadastradoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly fornecedorId: string | null;
  readonly fornecedor: string | null;
  /** Oito digitos, sem mascara; a tela formata. */
  readonly cep: string | null;
  readonly logradouro: string;
  readonly numero: string;
  readonly bairro: string;
  readonly municipio: string;
  readonly uf: string | null;
  readonly ativo: boolean;
}

export interface ProdutoCadastradoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly categoria: string;
  readonly unidade: string;
  readonly ativo: boolean;
}

/* ==========================================================================
 * As linhas, exatamente como a API as devolve
 * ======================================================================= */

export interface EquipamentoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly tipo: string | null;
  readonly modelo: string | null;
  readonly fabricante: string | null;
  readonly tipoMedidor: string;
  readonly origemDoMedidor: string;
  readonly capacidadeTanqueL: number | null;
  readonly consumoMinimo: number | null;
  readonly consumoMaximo: number | null;
  readonly status: string | null;
  readonly motivo: string | null;
  readonly obraId: string | null;
  readonly obra: string | null;
  readonly centroCustoId: string | null;
  readonly centroCusto: string | null;
  readonly ultimaLeitura: number | null;
  readonly ultimaLeituraEm: string | null;
  /** O que o motor queima, da ficha (0060). */
  readonly combustivel?: string | null;
  /** O produto do último abastecimento de combustível da máquina. */
  readonly ultimoProdutoId?: string | null;
  /** O código do dia a dia da empresa, "CB-07" (0125): etiqueta no seletor da máquina. */
  readonly codigoInterno?: string | null;
  /** A placa, sem hífen (0060): etiqueta no seletor da máquina. */
  readonly placa?: string | null;
}

/**
 * Uma linha do livro do tanque.
 *
 * `estornadoEm` e `ehContraLancamento` vêm da consulta, e não de uma segunda
 * chamada: a tela precisa dos dois para decidir o que mostrar, e buscá-los à
 * parte deixaria a lista piscando entre dois estados certos.
 */
export interface MovimentoDaApi {
  readonly id: string;
  readonly tipo: string;
  readonly quantidadeL: string;
  readonly sentido: number;
  readonly precoLitro: string | null;
  readonly ocorridoEm: string;
  readonly documento: string | null;
  readonly observacao: string | null;
  readonly fornecedor: string | null;
  readonly deAbastecimento: boolean;
  readonly estornadoEm: string | null;
  readonly motivoDoEstorno: string | null;
  readonly ehContraLancamento: boolean;
  /** 0059: a linha nasceu de uma correção ou de um balanço aprovado. */
  readonly ajusteTipo: 'correcao' | 'balanco' | null;
  readonly ajusteSolicitadoPor: string | null;
  readonly ajusteSolicitadoEm: string | null;
  readonly ajusteAprovadoPor: string | null;
  readonly ajusteAprovadoEm: string | null;
  /** Há um pedido de correção desta linha esperando aprovação. */
  readonly temCorrecaoPendente: boolean;
  /** 0062: a linha de transferência carrega a viagem inteira. */
  readonly transferenciaSituacao: 'em_transito' | 'concluida' | null;
  readonly outroTanque: string | null;
  readonly caminhao: string | null;
  readonly transferenciaSaidaL: number | null;
  readonly transferenciaChegadaL: number | null;
  readonly transferenciaPerdaL: number | null;
  readonly transferenciaJustificativa: string | null;
  readonly transferenciaSaidaPor: string | null;
  readonly transferenciaChegadaPor: string | null;
  readonly transferenciaChegouEm: string | null;
}

/** 0062: a transferência em duas etapas — saída, estrada, chegada. */
export interface TransferenciaDaApi {
  readonly id: string;
  readonly situacao: 'em_transito' | 'concluida';
  readonly origemId: string;
  readonly origemCodigo: string;
  readonly origemNome: string;
  readonly origemFilial: string | null;
  readonly destinoId: string;
  readonly destinoCodigo: string;
  readonly destinoNome: string;
  readonly destinoFilial: string | null;
  readonly caminhaoCodigo: string | null;
  readonly caminhaoDescricao: string | null;
  readonly quantidadeSaidaL: number;
  readonly saiuEm: string;
  readonly saidaPor: string | null;
  readonly observacaoSaida: string | null;
  readonly quantidadeChegadaL: number | null;
  readonly chegouEm: string | null;
  readonly chegadaPor: string | null;
  readonly perdaL: number | null;
  readonly justificativaDaPerda: string | null;
}

/** 0059: um pedido de correção ou de balanço, e o que foi decidido sobre ele. */
export interface AjusteDaApi {
  readonly id: string;
  readonly tipo: 'correcao' | 'balanco';
  readonly situacao: 'aguardando aprovacao' | 'aplicando' | 'aprovado' | 'recusado' | 'cancelado';
  readonly tanqueId: string;
  readonly tanqueCodigo: string;
  readonly tanqueNome: string;
  readonly movimentoId: string | null;
  readonly movimentoTipo: string | null;
  readonly movimentoQuantidadeL: number | null;
  readonly movimentoOcorridoEm: string | null;
  readonly quantidadeCorretaL: number | null;
  readonly volumeContadoL: number | null;
  readonly contadoEm: string | null;
  readonly volumeDoLivroL: number | null;
  readonly diferencaL: number;
  readonly valorEstimado: number | null;
  readonly motivo: string;
  readonly solicitadoPor: string | null;
  readonly solicitadoEm: string;
  readonly decididoPor: string | null;
  readonly decididoEm: string | null;
  readonly notaDaDecisao: string | null;
  /** Fui eu que pedi: posso cancelar, e não posso decidir. */
  readonly meu: boolean;
  /** A minha alçada de ajuste cobre o valor deste pedido. */
  readonly cabeNaMinhaAlcada: boolean;
}

export interface TanqueDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly tipo: string;
  readonly padrao: boolean;
  readonly ativo: boolean;
  readonly capacidadeL: number;
  readonly saldoCalculadoL: number;
  readonly toleranciaL: number;
  readonly ultimoMovimentoEm: string | null;
  readonly ultimaMedicaoEm: string | null;
  readonly ultimaMedicaoObservadoL: number | null;
  readonly ultimaMedicaoCalculadoL: number | null;
  readonly ultimaMedicaoDiferencaL: number | null;
  readonly dentroDaTolerancia: boolean | null;
  readonly produtoId: string;
  readonly produto: string;
  readonly unidade: string;
  readonly precoLitro: number | null;
  readonly precoEm: string | null;
  readonly obra: string | null;
}

export interface PostoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly municipio: string;
  readonly uf: string | null;
  readonly ativo: boolean;
  readonly fornecedorId: string | null;
  readonly fornecedor: string | null;
}

export interface ProdutoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly categoria: 'combustivel' | 'lubrificante' | 'arla';
  readonly unidade: string;
}

export interface ObraDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly tipo: string;
  readonly centroCustoId: string | null;
  readonly centroCusto: string | null;
}

export interface NomeadoDaApi {
  readonly id: string;
  readonly nome: string;
}

export interface CadastrosDaApi {
  readonly equipamentos: readonly EquipamentoDaApi[];
  readonly tanques: readonly TanqueDaApi[];
  readonly postos: readonly PostoDaApi[];
  readonly produtos: readonly ProdutoDaApi[];
  readonly obras: readonly ObraDaApi[];
  readonly centrosCusto: readonly NomeadoDaApi[];
  readonly fornecedores: readonly NomeadoDaApi[];
  readonly usuarios: readonly NomeadoDaApi[];
}

export interface AbastecimentoDaApi {
  readonly id: string;
  readonly tipo: 'interno' | 'externo';
  readonly ocorridoEm: string;
  readonly quantidadeL: number;
  readonly precoLitro: number | null;
  readonly valorTotal: number | null;
  readonly comprovante: string | null;
  readonly observacao: string | null;
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly equipamentoTipo: string | null;
  readonly produto: string;
  /**
   * `combustivel`, `arla`, `lubrificante` ou `outro` — a categoria do produto
   * deste lançamento.
   *
   * Ela viaja até a tela por uma razão só: a média de litros por hora é de
   * COMBUSTÍVEL. O banco já separa a cadeia de consumo por produto (migração
   * 0011), mas quem agrega por máquina precisa saber qual linha entra na
   * conta — somar ARLA no mesmo L/h põe litros que não são do diesel no
   * numerador e conta as mesmas horas duas vezes no denominador.
   */
  readonly produtoCategoria: string;
  readonly tanque: string | null;
  readonly posto: string | null;
  readonly ordemNumero: number | null;
  readonly obra: string | null;
  readonly centroCusto: string | null;
  readonly operador: string | null;
  readonly responsavel: string | null;
  readonly medidor: number | null;
  readonly tipoMedidor: string | null;
  readonly medidorAnterior: number | null;
  readonly variacaoDoMedidor: number | null;
  readonly consumoPorUnidade: number | null;
  readonly litrosNoIntervalo: number | null;
  readonly abastecimentosSemLeitura: number | null;
  readonly limitacao: string | null;
  readonly confiavel: boolean | null;
  /**
   * O desvio de faixa, apurado NO SERVIDOR (migração 0011).
   *
   * `true` fora, `false` dentro, `null` não há como saber — sem consumo
   * apurado, sem faixa cadastrada, ou produto que a faixa não descreve (ela é
   * de litro de combustível por hora; julgar ARLA por ela acusaria desvio em
   * toda máquina que usa ARLA).
   *
   * `null` e `false` NÃO são a mesma coisa, e é por isso que o campo não é
   * `boolean`: `false` afirma que está dentro, e afirmar isso sem faixa
   * cadastrada é a mentira mais fácil de acreditar nesta tela.
   */
  readonly foraDaFaixa: boolean | null;
  readonly produtoId: string | null;
  /**
   * A CONFERENCIA (0122): quando alguem olhou este lancamento e disse que
   * esta certo, e quem foi. Nulos enquanto ninguem olhou — e e a ausencia
   * deles que o indicador "Para conferir" conta.
   *
   * Conferir NAO aprova, NAO corrige e NAO muda numero nenhum. O lancamento
   * conferido continua o mesmo no livro; o que passa a existir e a assinatura
   * de quem olhou.
   */
  readonly conferidoEm?: string | null;
  readonly conferidoPorNome?: string | null;
  /**
   * DE ONDE O LANCAMENTO VEIO: `true` quando passou pela fila do celular
   * (0069), `false` quando foi digitado na mesa.
   *
   * Nao e campo novo no banco — e a leitura de `chave_de_envio`, que so
   * existe no lancamento que atravessou a fila. Quem le a lista precisa
   * saber: o numero digitado em pe, na frente de servico, tem outro risco de
   * digitacao do que o numero conferido com o cupom na mao.
   */
  readonly doCelular?: boolean;
  readonly origemDaFaixa: string | null;
  readonly consumoMinimo: number | null;
  readonly consumoMaximo: number | null;
  /* 0087 · o que a correção precisa para abrir o formulário já preenchido, e
     o estorno, quando houve. Opcionais: a fila do celular e as massas de
     teste montam a linha sem eles. */
  readonly tanqueId?: string | null;
  readonly postoId?: string | null;
  readonly ordemId?: string | null;
  readonly obraId?: string | null;
  readonly centroCustoId?: string | null;
  readonly operadorId?: string | null;
  readonly responsavelId?: string | null;
  readonly cobrancaId?: string | null;
  /** Quando o abastecimento foi estornado; `null` quando não foi. */
  readonly estornadoEm?: string | null;
  readonly motivoDoEstorno?: string | null;
  readonly estornadoPor?: string | null;
  /** O abastecimento estornado que este lançamento corrige. */
  readonly corrigeId?: string | null;
  /** O lançamento que corrigiu este. */
  readonly corrigidoPorId?: string | null;
  /**
   * A máquina por extenso (27/10/2026): a descrição da unidade ou, sem ela,
   * fabricante e modelo do catálogo. Opcional porque a fila do celular e as
   * massas de teste montam a linha sem ela.
   */
  readonly equipamentoDescricao?: string | null;
  /**
   * QUEM REGISTROU o lançamento e quando (27/10/2026) — a sessão que gravou a
   * linha, lida da trilha de auditoria. Não é o operador (quem estava na
   * máquina) nem o responsável (quem liberou o diesel): esses são dados que
   * quem lança escolhe. Nulo quando a trilha não tem a inserção.
   */
  readonly registradoEm?: string | null;
  readonly registradoPor?: string | null;
  /** Os papéis em vigor de quem registrou, "ADMINISTRADOR, GESTOR". */
  readonly registradoPorPerfil?: string | null;
  /** Veio da folha de campo lida pela IA (0035). */
  readonly daFolha?: boolean;
}

export interface OrdemDaApi {
  readonly id: string;
  readonly numero: number;
  readonly situacao: 'aberta' | 'atendida' | 'cancelada';
  readonly autorizadaEm: string;
  readonly validadeEm: string | null;
  readonly quantidadeAutorizadaL: number;
  readonly quantidadeEntregueL: number;
  readonly saldoAutorizadoL: number;
  readonly abastecimentos: number;
  readonly ultimoAbastecimentoEm: string | null;
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly tanqueId: string;
  readonly tanque: string;
  readonly motivoCancelamento: string | null;
  readonly autorizadoPor: string | null;
}

export interface CobrancaDaApi {
  readonly id: string;
  readonly documento: string;
  readonly competencia: string | null;
  readonly situacao: 'aberta' | 'conciliada' | 'divergente';
  readonly valorCobrado: number;
  readonly valorLancado: number;
  readonly diferenca: number;
  readonly abastecimentos: number;
  readonly litros: number;
  readonly fornecedor: string;
  readonly posto: string | null;
}

export interface LubrificanteDaApi {
  readonly id: string;
  readonly ocorridoEm: string;
  readonly quantidade: number;
  /**
   * O preço por litro ou por quilo, e o total que o BANCO calculou (coluna
   * gerada, migração 0011).
   *
   * `null` é honesto e não é zero: enquanto o módulo 07 não der custo médio ao
   * produto, o preço só existe se alguém o digitou. Zero diria que o óleo saiu
   * de graça, e quem lesse o custo por hora leria um número menor que o real
   * sem ter como saber.
   */
  readonly precoUnitario: number | null;
  readonly valorTotal: number | null;
  readonly ponto: string | null;
  readonly observacao: string | null;
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly produto: string;
  readonly unidade: string;
  readonly responsavel: string | null;
  readonly medidor: number | null;
  readonly tipoMedidor: string | null;
}

/* ==========================================================================
 * O que se manda
 * ======================================================================= */

export interface EnvioDeAbastecimento {
  readonly tipo: 'interno' | 'externo';
  readonly equipamentoId: string;
  readonly produtoId: string;
  readonly ocorridoEm: string;
  readonly quantidadeL: number;
  readonly tanqueId?: string;
  readonly ordemId?: string;
  readonly postoId?: string;
  readonly precoLitro?: number;
  readonly comprovante?: string;
  readonly medidor?: number;
  readonly operadorId?: string;
  readonly responsavelId?: string;
  readonly obraId?: string;
  readonly centroCustoId?: string;
  readonly observacao?: string;
  readonly motivo?: string;
}

export interface EnvioDeOrdem {
  readonly equipamentoId: string;
  readonly tanqueId: string;
  readonly quantidadeAutorizadaL: number;
  readonly validadeEm?: string;
  readonly obraId?: string;
  readonly centroCustoId?: string;
  readonly observacao?: string;
  readonly motivo?: string;
}

export interface EnvioDeMedicao {
  readonly medidoEm: string;
  readonly volumeObservadoL: number;
  readonly observacao?: string;
  readonly motivo?: string;
}

export interface EnvioDeRecebimento {
  readonly fornecedorId: string;
  readonly quantidadeL: number;
  readonly precoLitro?: number;
  readonly ocorridoEm: string;
  readonly documento?: string;
  readonly motivo?: string;
}

export interface EnvioDeCobranca {
  readonly fornecedorId: string;
  readonly documento: string;
  readonly valorTotal: number;
  readonly postoId?: string;
  readonly competencia?: string;
  readonly motivo?: string;
}

export interface EnvioDeLubrificante {
  readonly equipamentoId: string;
  readonly produtoId: string;
  readonly quantidade: number;
  /** Preço por litro ou por quilo. Ausente quando quem lança não o tem à mão. */
  readonly precoUnitario?: number;
  /** Decisão 13: com depósito, a aplicação baixa o estoque. */
  readonly depositoId?: string;
  readonly ocorridoEm: string;
  readonly ponto?: string;
  readonly medidor?: number;
  readonly motivo?: string;
}

/**
 * Lista cortada pelo servidor: as linhas, quantas existem ao todo e por quanto
 * o servidor cortou. As tres coisas juntas, porque separadas a tela consegue
 * mostrar a soma e esquecer o recorte — que foi exatamente o que aconteceu.
 */
export interface ListaCortada {
  readonly total: number;
  readonly limite: number;
}

export interface ListaDeAbastecimentos extends ListaCortada {
  readonly abastecimentos: readonly AbastecimentoDaApi[];
}

export interface ListaDeLubrificantes extends ListaCortada {
  readonly lubrificantes: readonly LubrificanteDaApi[];
}

/* ==========================================================================
 * As chamadas
 * ======================================================================= */

export const api = {
  cadastros: () => pedir<CadastrosDaApi>('/combustivel/cadastros'),

  /**
   * A lista vem CORTADA, e a resposta diz por quanto.
   *
   * O corte sempre existiu (o backend usa 200 quando ninguem pede outro
   * numero); o que nao existia era a tela saber disso. Os indicadores do
   * modulo — volume, gasto, consumo por maquina, externos a conciliar — sao
   * somas sobre esta lista, e passados 200 lancamentos todos ficavam errados
   * em silencio, com o rotulo "no periodo carregado" sobre um periodo que nao
   * existe (a tela nao tem filtro de data).
   *
   * `total` e `limite` sobem junto com as linhas para a tela poder escrever o
   * recorte onde ela mostra a soma. Numero cortado sem o recorte ao lado nao e
   * indicador, e um palpite.
   */
  abastecimentos: (filtro: { readonly limite?: number; readonly semCobranca?: boolean } = {}) => {
    const parametros = new URLSearchParams();
    if (filtro.limite !== undefined) parametros.set('limite', String(filtro.limite));
    if (filtro.semCobranca) parametros.set('semCobranca', 'true');
    const busca = parametros.toString();
    return pedir<ListaDeAbastecimentos>(
      `/combustivel/abastecimentos${busca ? `?${busca}` : ''}`,
    );
  },

  lancarAbastecimento: (envio: EnvioDeAbastecimento) =>
    enviar<{ abastecimento: AbastecimentoDaApi }>('/combustivel/abastecimentos', envio),

  /** ADR-008, 0087: estornar o abastecimento — o volume volta ao tanque, e o motivo fica escrito. */
  estornarAbastecimento: (abastecimentoId: string, motivo: string) =>
    enviar<{ estornoId: string; abastecimento: AbastecimentoDaApi }>(`/combustivel/abastecimentos/${abastecimentoId}/estorno`, { motivo }),

  /**
   * 0126: APAGAR o abastecimento de vez. Não é o estorno: o estorno deixa a
   * linha visível e anulada, e esta a tira da lista. Só quem tem
   * `abastecimento.excluir`, que é o administrador da empresa.
   */
  excluirAbastecimento: (abastecimentoId: string, motivo: string) =>
    enviar<{ id: string; excluido: boolean }>(`/combustivel/abastecimentos/${abastecimentoId}/exclusao`, { motivo }),

  /**
   * 0122: marcar o lançamento como conferido. Sem corpo, de propósito — não
   * há nada para a tela escolher: quem confere é a sessão e quando é agora.
   *
   * A permissão é `abastecimento.conferir`, e não a de lançar: quem lança não
   * é quem confere.
   */
  conferirAbastecimento: (abastecimentoId: string) =>
    enviar<{ abastecimento: AbastecimentoDaApi }>(`/combustivel/abastecimentos/${abastecimentoId}/conferencia`, {}),

  /** 0087: corrigir é estornar o errado e lançar o certo, numa transação só. */
  corrigirAbastecimento: (abastecimentoId: string, motivo: string, abastecimento: EnvioDeAbastecimento) =>
    enviar<{ estornoId: string; corrigidoId: string; abastecimento: AbastecimentoDaApi }>(
      `/combustivel/abastecimentos/${abastecimentoId}/correcao`,
      { motivo, abastecimento },
    ),

  tanques: () => pedir<{ tanques: readonly TanqueDaApi[] }>('/combustivel/tanques'),

  medirTanque: (tanqueId: string, envio: EnvioDeMedicao) =>
    enviar<{ id: string }>(`/combustivel/tanques/${tanqueId}/medicoes`, envio),

  receberNoTanque: (tanqueId: string, envio: EnvioDeRecebimento) =>
    enviar<{ id: string }>(`/combustivel/tanques/${tanqueId}/recebimentos`, envio),

  /**
   * O livro do tanque, linha a linha.
   *
   * Existe por causa do estorno: o saldo era um número sem como abrir, e quem
   * visse 19.000 L num tanque de 2.000 não tinha por onde descobrir QUAL
   * lançamento estava errado.
   */
  movimentosDoTanque: (tanqueId: string) =>
    pedir<{ movimentos: readonly MovimentoDaApi[] }>(`/combustivel/tanques/${tanqueId}/movimentos`),

  /** ADR-008: corrigir é lançar contra, e o motivo fica escrito. */
  estornarMovimento: (movimentoId: string, motivo: string) =>
    enviar<{ id: string; contraLancamentoId: string }>(`/combustivel/movimentos/${movimentoId}/estorno`, { motivo }),

  /* 0059 · pedir, aprovar, recusar e cancelar ajuste; excluir, só a plataforma. */
  ajustes: () => pedir<{ ajustes: readonly AjusteDaApi[] }>('/combustivel/ajustes'),

  pedirCorrecao: (movimentoId: string, envio: { quantidadeCorretaL: number; motivo: string }) =>
    enviar<{ id: string }>(`/combustivel/movimentos/${movimentoId}/correcao`, envio),

  pedirBalanco: (tanqueId: string, envio: { volumeContadoL: number; contadoEm: string; motivo: string }) =>
    enviar<{ id: string }>(`/combustivel/tanques/${tanqueId}/balancos`, envio),

  aprovarAjuste: (ajusteId: string, nota?: string) =>
    enviar<{ id: string }>(`/combustivel/ajustes/${ajusteId}/aprovacao`, nota ? { nota } : {}),

  recusarAjuste: (ajusteId: string, nota: string) =>
    enviar<{ id: string }>(`/combustivel/ajustes/${ajusteId}/recusa`, { nota }),

  cancelarAjuste: (ajusteId: string) => enviar<{ id: string }>(`/combustivel/ajustes/${ajusteId}/cancelamento`, {}),

  excluirMovimento: (movimentoId: string, motivo: string) =>
    enviar<{ id: string }>(`/combustivel/movimentos/${movimentoId}/exclusao`, { motivo }),

  /** Decisão 6: do pátio para o comboio. A saída e a entrada nascem juntas. */
  transferir: (envio: {
    origemId: string;
    destinoId: string;
    caminhaoId?: string;
    quantidadeL: number;
    ocorridoEm: string;
    chegouJunto: boolean;
    motivo?: string;
  }) => enviar<{ transferenciaId: string; situacao: 'em_transito' | 'concluida' }>('/combustivel/transferencias', envio),

  /* 0062 · as transferências, e a chegada de uma em trânsito. */
  transferencias: () => pedir<{ transferencias: readonly TransferenciaDaApi[] }>('/combustivel/transferencias'),

  registrarChegada: (id: string, envio: { quantidadeL: number; chegouEm: string; justificativa?: string }) =>
    enviar<{ id: string }>(`/combustivel/transferencias/${id}/chegada`, envio),

  ordens: () => pedir<{ ordens: readonly OrdemDaApi[] }>('/combustivel/ordens'),

  autorizarOrdem: (envio: EnvioDeOrdem) => enviar<{ id: string; numero: number }>('/combustivel/ordens', envio),

  cancelarOrdem: (ordemId: string, motivo: string) =>
    enviar<{ id: string }>(`/combustivel/ordens/${ordemId}/cancelamento`, { motivo }),

  postos: () => pedir<{ postos: readonly PostoDaApi[] }>('/combustivel/postos'),

  cobrancas: () => pedir<{ cobrancas: readonly CobrancaDaApi[] }>('/combustivel/cobrancas'),

  lancarCobranca: (envio: EnvioDeCobranca) => enviar<{ id: string }>('/combustivel/cobrancas', envio),

  conciliarCobranca: (cobrancaId: string, abastecimentos: readonly string[]) =>
    enviar<{ ligados: number; situacao: string; diferenca: number }>(
      `/combustivel/cobrancas/${cobrancaId}/abastecimentos`,
      { abastecimentos },
    ),

  lubrificantes: () => pedir<ListaDeLubrificantes>('/combustivel/lubrificantes'),

  registrarLubrificante: (envio: EnvioDeLubrificante) => enviar<{ id: string }>('/combustivel/lubrificantes', envio),

  /* ------------------------------------------------ os cadastros do módulo
   *
   * Tanque, posto e produto passaram a entrar pela tela — antes disso só por
   * SQL, e com o banco vazio o módulo inteiro não tinha o que mostrar. A
   * decisão 6 do dono: "todas as situações podem acontecer, devem ser
   * parametrizável no sistema".
   *
   * Não há `excluir`. O backend não tem rota de `DELETE` e isso é decisão, não
   * omissão: apagar um tanque levaria junto o livro dele. O que existe é
   * inativar, que é um `PATCH`. */
  tanquesCadastrados: () => pedir<{ itens: readonly TanqueCadastradoDaApi[] }>('/combustivel/cadastros/tanques'),
  criarTanque: (envio: unknown) => enviar<TanqueCadastradoDaApi>('/combustivel/cadastros/tanques', envio),
  alterarTanque: (id: string, envio: unknown) =>
    alterar<TanqueCadastradoDaApi>(`/combustivel/cadastros/tanques/${id}`, envio),

  postosCadastrados: () => pedir<{ itens: readonly PostoCadastradoDaApi[] }>('/combustivel/cadastros/postos'),
  criarPosto: (envio: unknown) => enviar<PostoCadastradoDaApi>('/combustivel/cadastros/postos', envio),
  alterarPosto: (id: string, envio: unknown) =>
    alterar<PostoCadastradoDaApi>(`/combustivel/cadastros/postos/${id}`, envio),

  produtosCadastrados: () => pedir<{ itens: readonly ProdutoCadastradoDaApi[] }>('/combustivel/cadastros/produtos'),
  criarProduto: (envio: unknown) => enviar<ProdutoCadastradoDaApi>('/combustivel/cadastros/produtos', envio),
  alterarProduto: (id: string, envio: unknown) =>
    alterar<ProdutoCadastradoDaApi>(`/combustivel/cadastros/produtos/${id}`, envio),
} as const;

/* `ehFaltaDeSistema` e `porQueEstaEmDemonstracao` moravam aqui e foram
   apagadas: a massa que elas explicavam não existe mais, não havia um só
   consumidor delas em `src/`, e a segunda ainda sabia escrever a faixa que
   avisava que os números da tela eram inventados — uma frase que, num módulo
   ligado ao banco, só poderia aparecer por engano. */
